import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { PagedResult, PaginationParams } from '../../core/models/api.models';
import {
  ProjectDto,
  ProjectDetailDto,
  ProjectMemberDto,
  ProjectActivityDto,
  CreateProjectRequest,
  UpdateProjectRequest,
  DuplicateProjectRequest,
  CreateFromTemplateRequest,
  AddMemberRequest,
  UpdateMemberRequest,
  ProjectListParams
} from './project.models';

@Injectable({ providedIn: 'root' })
export class ProjectService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/projects';

  list(params: ProjectListParams = {}): Observable<PagedResult<ProjectDto>> {
    return this.api.getPaged<ProjectDto>(this.base, params as unknown as PaginationParams);
  }

  getById(id: string): Observable<ProjectDetailDto> {
    return this.api.get<ProjectDetailDto>(`${this.base}/${id}`);
  }

  create(request: CreateProjectRequest): Observable<ProjectDetailDto> {
    return this.api.post<ProjectDetailDto>(this.base, request);
  }

  update(id: string, request: UpdateProjectRequest): Observable<ProjectDetailDto> {
    return this.api.put<ProjectDetailDto>(`${this.base}/${id}`, request);
  }

  delete(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${id}`);
  }

  getMembers(projectId: string): Observable<ProjectMemberDto[]> {
    return this.api.get<ProjectMemberDto[]>(`${this.base}/${projectId}/members`);
  }

  addMember(projectId: string, request: AddMemberRequest): Observable<ProjectMemberDto> {
    return this.api.post<ProjectMemberDto>(`${this.base}/${projectId}/members`, request);
  }

  updateMember(projectId: string, memberId: string, request: UpdateMemberRequest): Observable<ProjectMemberDto> {
    return this.api.put<ProjectMemberDto>(`${this.base}/${projectId}/members/${memberId}`, request);
  }

  removeMember(projectId: string, memberId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${projectId}/members/${memberId}`);
  }

  /** Server toggles favorite for the current user and returns the new state. */
  toggleFavorite(projectId: string): Observable<{ isFavorite: boolean }> {
    return this.api.post<{ isFavorite: boolean }>(`${this.base}/${projectId}/favorite`, {});
  }

  /** @deprecated Prefer toggleFavorite — kept for callers that expect add/remove naming. */
  addFavorite(projectId: string): Observable<{ isFavorite: boolean }> {
    return this.toggleFavorite(projectId);
  }

  removeFavorite(projectId: string): Observable<{ isFavorite: boolean }> {
    return this.toggleFavorite(projectId);
  }

  archive(projectId: string, archive = true): Observable<ProjectDetailDto> {
    return this.api.post<ProjectDetailDto>(`${this.base}/${projectId}/archive`, { archive });
  }

  /** Restore an archived project (archive = false). */
  restore(projectId: string): Observable<ProjectDetailDto> {
    return this.archive(projectId, false);
  }

  duplicate(projectId: string, request: DuplicateProjectRequest = {}): Observable<ProjectDetailDto> {
    return this.api.post<ProjectDetailDto>(`${this.base}/${projectId}/duplicate`, request);
  }

  createFromTemplate(templateId: string, request: CreateFromTemplateRequest): Observable<ProjectDetailDto> {
    return this.api.post<ProjectDetailDto>(`${this.base}/${templateId}/from-template`, request);
  }

  setAsTemplate(projectId: string, isTemplate = true): Observable<ProjectDetailDto> {
    return this.api.post<ProjectDetailDto>(`${this.base}/${projectId}/set-as-template`, { isTemplate });
  }

  getActivity(projectId: string, page = 1, pageSize = 50): Observable<ProjectActivityDto[]> {
    return this.api.getPaged<ProjectActivityDto>(`${this.base}/${projectId}/activity`, { page, pageSize })
      .pipe(map(r => r.items ?? []));
  }

  /** Assign or clear the project's issue workflow (defensive — endpoint may vary by backend version). */
  assignWorkflow(projectId: string, issueWorkflowId: string | null): Observable<ProjectDetailDto> {
    return this.api.put<ProjectDetailDto>(`${this.base}/${projectId}/workflow`, { issueWorkflowId });
  }
}
